import { LitElement, css, html } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import {
  findProductsByCompactSku,
  fullSearchUrlForQuery,
  looksLikeCompactSkuQuery,
  normalizeStorefrontSearchQuery,
  resolveCompactSkuQuery,
} from '@nova/search-sku'

function storefrontRootPath() {
  const root = window.Shopify?.routes?.root
  if (typeof root === 'string' && root.length) return root.endsWith('/') ? root : `${root}/`
  return '/'
}

/** Shopify caps predictive search at 10 per type when limit_scope is each. */
function predictiveSuggestSearchParams(searchTerm, resourceTypes) {
  const params = new URLSearchParams()
  params.set('q', searchTerm)
  params.set(
    'resources[type]',
    resourceTypes || 'query,product,collection,page',
  )
  params.set('resources[limit]', '10')
  params.set('resources[limit_scope]', 'each')
  params.set('resources[options][unavailable_products]', 'last')
  params.set(
    'resources[options][fields]',
    'title,product_type,variants.title,vendor,tag,variants.sku,variants.barcode',
  )
  return params
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, '')
    .trim()
}

function normalizeSuggestTerm(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
}

/** When the typed phrase returns queries but no products, try related suggest terms. */
function buildSuggestProductFallbackTerms(searchTerm, queries) {
  const terms = []
  const seen = new Set()
  const add = (value) => {
    const term = String(value || '').trim()
    const key = normalizeSuggestTerm(term)
    if (!key || seen.has(key)) return
    seen.add(key)
    terms.push(term)
  }

  const original = String(searchTerm || '').trim()
  const normalized = normalizeStorefrontSearchQuery(original)

  for (const query of queries.slice(0, 5)) {
    add(query?.text)
    add(stripHtml(query?.styled_text))
  }

  if (normalized) {
    add(normalized)
    if (normalized.includes('-')) {
      add(normalized.replace(/-/g, ' '))
    }
  }

  if (original) {
    const compact = original.replace(/\s+/g, '')
    if (compact.length > 2) add(compact)

    const words = original.split(/\s+/).filter(Boolean)
    if (words.length > 1) {
      add(words.join(''))
    }
  }

  if (!original) return terms

  const originalKey = normalizeSuggestTerm(original)
  return terms.filter((term) => normalizeSuggestTerm(term) !== originalKey)
}

function mergeSuggestProducts(existing, incoming) {
  const merged = [...(existing || [])]
  const ids = new Set(merged.map((product) => product.id).filter(Boolean))

  for (const product of incoming || []) {
    if (!product?.id || ids.has(product.id)) continue
    ids.add(product.id)
    merged.push(product)
  }

  return merged
}

async function fetchSuggestPayload(searchTerm, resourceTypes, signal) {
  const base = storefrontRootPath()
  const url = `${base}search/suggest.json?${predictiveSuggestSearchParams(searchTerm, resourceTypes)}`
  const response = await fetch(url, { signal })
  if (!response.ok) {
    throw new Error(`Search suggest failed: ${response.status}`)
  }
  const data = await response.json()
  if (!data?.resources?.results) {
    throw new Error('Search suggest returned an unexpected payload')
  }
  return data
}

async function fetchSuggestProducts(searchTerm, signal) {
  const data = await fetchSuggestPayload(searchTerm, 'product', signal)
  return data.resources?.results?.products ?? []
}

class NovaSearchForm extends LitElement {
  static properties = {
    url: { type: String },
    value: { type: String },
    openState: { type: String },
  }

  constructor() {
    super()
    this.searchData = {}
    this.searchTerm = ''
    this.openState = 'closed'
  }

  connectedCallback() {
    super.connectedCallback()

    // Listen to parent drawer events for more reliable Safari compatibility
    this.drawer = this.closest('.js-header-drawer[data-type="search"]')
    if (this.drawer) {
      this.handleDrawerShow = () => {
        this.openState = 'open'
        this.requestUpdate()
        // Focus the search input so the user can type immediately without an extra click
        const focusInput = () => {
          const input = this.shadowRoot?.querySelector('.header-search__input')
          if (input && typeof input.focus === 'function') input.focus()
        }
        requestAnimationFrame(() => setTimeout(focusInput, 50))
      }
      this.handleDrawerHide = () => {
        this.openState = 'closed'
        this.requestUpdate()
      }

      this.drawer.addEventListener('sl-show', this.handleDrawerShow)
      this.drawer.addEventListener('sl-hide', this.handleDrawerHide)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.drawer) {
      this.drawer.removeEventListener('sl-show', this.handleDrawerShow)
      this.drawer.removeEventListener('sl-hide', this.handleDrawerHide)
    }
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties)
    if (this.value) {
      this.getSearchResults(this.value)
    }
  }

  searchOnChange(event) {
    // More robust check for Safari compatibility
    const drawer = this.closest('.js-header-drawer[data-type="search"]')
    const isDrawerOpen = Boolean(
      drawer &&
      (drawer.hasAttribute('is-open') ||
        drawer.hasAttribute('open') ||
        drawer.open === true),
    )

    // Only return early if we're explicitly closed AND the drawer isn't open
    if (this.openState === 'closed' && !isDrawerOpen) return

    const inputHost = event.currentTarget ?? event.target
    const searchValue =
      typeof inputHost?.value === 'string' ? inputHost.value : ''
    this.value = searchValue
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(searchValue)
    }, 300)
  }

  async onSearchSubmit(event) {
    if (this._skipSkuResolveSubmit) return

    const form = event.currentTarget
    const input = form?.querySelector?.('.header-search__input')
    if (!input) return

    const normalized =
      normalizeStorefrontSearchQuery(input.value) ||
      String(input.value || '').trim()
    if (normalized && normalized !== String(input.value || '').trim()) {
      input.value = normalized
      this.value = normalized
    }

    if (!looksLikeCompactSkuQuery(normalized)) return

    event.preventDefault()
    if (this._submitSkuAbort) this._submitSkuAbort.abort()
    this._submitSkuAbort = new AbortController()

    try {
      const resolvedSku = await resolveCompactSkuQuery(
        normalized,
        this._submitSkuAbort.signal,
      )
      const nextQuery = resolvedSku || normalized
      input.value = nextQuery
      this.value = nextQuery
      this._skipSkuResolveSubmit = true
      window.location.assign(fullSearchUrlForQuery(nextQuery))
    } catch (error) {
      if (error?.name === 'AbortError') return
      this._skipSkuResolveSubmit = true
      window.location.assign(fullSearchUrlForQuery(normalized))
    }
  }

  async getSearchResults(searchTerm) {
    const normalizedTerm = normalizeStorefrontSearchQuery(searchTerm)
    const queryTerm = normalizedTerm || String(searchTerm || '').trim()

    if (!queryTerm.length) {
      if (this._suggestAbort) this._suggestAbort.abort()
      this.searchData = {}
      this.searchTerm = ''

      this.requestUpdate()
      return
    }

    if (this._suggestAbort) this._suggestAbort.abort()
    this._suggestAbort = new AbortController()
    const { signal } = this._suggestAbort
    const requestId = (this._suggestRequestId = (this._suggestRequestId || 0) + 1)

    try {
      const data = await fetchSuggestPayload(queryTerm, null, signal)
      if (requestId !== this._suggestRequestId) return

      let products = data.resources?.results?.products ?? []

      if (products.length === 0) {
        const fallbackTerms = buildSuggestProductFallbackTerms(
          queryTerm,
          data.resources?.results?.queries ?? [],
        )
        const rawLimit = Number(window.predictiveSearchSettings?.productLimit)
        const productCap =
          Number.isFinite(rawLimit) && rawLimit > 0
            ? Math.min(10, Math.floor(rawLimit))
            : 8

        const fallbackBatches = await Promise.all(
          fallbackTerms.slice(0, 4).map((term) =>
            fetchSuggestProducts(term, signal).catch(() => []),
          ),
        )
        if (requestId !== this._suggestRequestId) return

        for (const batch of fallbackBatches) {
          products = mergeSuggestProducts(products, batch)
          if (products.length >= productCap) break
        }

        if (
          products.length === 0 &&
          looksLikeCompactSkuQuery(queryTerm)
        ) {
          const compactMatches = await findProductsByCompactSku(
            queryTerm,
            signal,
          ).catch(() => [])
          if (requestId !== this._suggestRequestId) return
          products = mergeSuggestProducts(products, compactMatches)
        }

        if (products.length > 0) {
          data.resources.results.products = products.slice(0, productCap)
        }
      }

      this.searchData = data
      this.searchTerm = queryTerm
      this.requestUpdate()
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.error('Search suggest error:', error.message)
      if (requestId !== this._suggestRequestId) return
      this.searchData = {}
      this.searchTerm = queryTerm
      this.requestUpdate()
    }
  }

  closeSearchDrawer() {
    document.dispatchEvent(new CustomEvent('nova:searchDrawer:close'), {
      bubbles: true,
    })
  }

  render() {
    return html`
      <form
        class="header-search__form"
        action=${this.url}
        method="get"
        role="search"
        @submit=${this.onSearchSubmit}
      >
        <div class="header-search__field">
          <sl-input
            class="header-search__input"
            id="Search-In-Modal"
            type="search"
            name="q"
            value=${this.value}
            placeholder=${window.searchStrings.search}
            label=${window.searchStrings.enter_to_search}
            @sl-input=${this.searchOnChange}
            @input=${this.searchOnChange}
          >
            <span class="header-search__icon" slot="prefix">
              ${paths.search}
            </span>
            <span class="header-search__icon" slot="suffix">
              ${paths.search}
            </span>
          </sl-input>
          <input type="hidden" name="options[prefix]" value="last" />
          <input type="hidden" name="options[fields]" value="title,product_type,variants.title,vendor,tag,variants.sku" />
          <sl-button class="header-search__cancel" @click=${this.closeSearchDrawer}>${window.searchStrings.cancel}</sl-button>
        </div>
      </form>
      <nova-search-result
        .searchData=${this.searchData}
        .searchTerm=${this.searchTerm}
        .openState=${this.openState}
      ></nova-search-result>
    `
  }

  static styles = [
    styles.variables,
    css`
      :host {
        width: 100%;
        height: 100%;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      :host::-webkit-scrollbar {
        display: none;
        width: 0;
      }

      :host .header-search__form {
        flex-shrink: 0;
        position: sticky;
        top: 0;
        z-index: 2;
        padding: var(--nova-space-md) var(--nova-space-page-margin) 0;
        background-color: var(--nova-brand-reverse);
      }

      :host .header-search__field {
        display: flex;
        align-items: center;
        position: relative;
      }

      :host .header-search__input {
        width: 100%;
        border: none;
        font-family: var(--nova-font-sans);
        font-size: var(--header-search-input-font-size-mobile, 16px);
        font-weight: var(--header-search-input-font-weight, 600);
      }

      :host .header-search__input::part(input) {
        padding: 0 var(--nova-space-md);
        height: var(--nova-space-2xl);
        font-family: var(--nova-font-sans);
        font-size: var(--header-search-input-font-size-mobile, 16px);
        font-weight: var(--header-search-input-font-weight, 600);
        line-height: 120%;
        text-transform: uppercase;
        letter-spacing: var(--nova-h4-letter-spacing, 0.96px);
      }

      :host .header-search__input::part(input)::placeholder {
        font-size: inherit;
        font-weight: inherit;
        opacity: 1;
      }

      @media screen and (min-width: 750px) {
        :host .header-search__input {
          font-size: var(--header-search-input-font-size-desktop, 18px);
        }

        :host .header-search__input::part(input) {
          font-size: var(--header-search-input-font-size-desktop, 18px);
        }
      }

      :host .header-search__input::part(form-control) {
        display: flex;
        flex-direction: column-reverse;
        gap: var(--nova-space-sm);
      }

      :host .header-search__input::part(form-control-label) {
        color: var(--nova-color-subdued);
        margin: 0;
        font-weight: 400;
        line-height: 140%;
      }

      :host .header-search__input::part(base) {
        border: none;
        border-radius: 12px;
        border-bottom: 1px solid var(--nova-brand-ink);
        height: var(--nova-space-2xl);
        padding: 5 var(--nova-space-2xs);
        --sl-input-focus-ring-color: var(--nova-brand-primary);
            box-shadow: 0 0 4px 0px #cacaca;
      }

      :host .header-search__input::part(suffix) {
        display: none;
        padding-right: var(--nova-space-md);
      }

      :host .header-search__input::part(prefix) {
        padding-left: var(--nova-space-xs);
      }

      :host .visually-hidden {
        position: absolute !important;
        overflow: hidden;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
        clip: rect(0 0 0 0);
        word-wrap: normal !important;
      }

      :host .header-search__label {
        display: block;
        padding: var(--nova-space-sm) 0 var(--nova-space-md);
        color: var(--nova-color-subdued);
      }

      :host .header-search__input:focus-visible {
        outline: none;
      }

      /* Clear default clear button from Internet Explorer */
      :host input[type='search']::-ms-clear {
        display: none;
        -webkit-appearance: none;
        width: 0;
        height: 0;
      }

      /* Clear default clear button from Chrome */
      :host input[type='search']::-webkit-search-cancel-button {
        display: none;
        -webkit-appearance: none;
      }

      ::slotted([slot='search-icon']) {
        padding-right: var(--nova-space-xs);
      }

      @media screen and (min-width: 750px) {
        ::slotted([slot='search-icon']) {
          display: none;
        }
      }

      :host .header-search__icon {
        color: var(--nova-brand-ink);
        margin: 0;
        height: 100%;
        display: flex;
        align-items: center;
      }

      :host .header-search__cancel {
        position: absolute;
        right: var(--nova-space-2xs);
        height: 22px;
        top: var(--nova-space-lg);
        display: flex;
        transform: translateY(-50%);
        box-sizing: border-box;
      }

      :host .header-search__cancel::part(label) {
        padding: var(--nova-space-2xs);
        border-radius: var(--nova-border-radius-l);
        font-size: 12px;
        line-height: 1;
        text-transform: uppercase;
        color: var(--nova-brand-subdued);
        background-color: var(--nova-brand-light);
        display: inline-block;
        height: 22px;
      }

      :host .header-search__cancel::part(base) {
        min-height: unset;
        border: none;
      }

      @media screen and (min-width: 768px) {
        :host .header-search__cancel {
          top: 41px;
        }
      }

      @media screen and (min-width: 1420px) {
        :host .header-search__cancel {
          display: none;
        }
        
        /* Safari-specific: Show cancel button on Safari even on desktop */
        @supports (-webkit-touch-callout: none) {
          :host .header-search__cancel {
            display: flex !important;
          }
        }

        :host .header-search__input::part(suffix) {
          display: inline-flex;
        }

        :host .header-search__input::part(prefix) {
          display: none;
        }
      }

      :host nova-search-result {
        flex: 1 1 auto;
        min-height: 0;
        margin: var(--nova-space-md) 0 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
    `,
  ]
}

customElements.define('nova-search-form', NovaSearchForm)
